/**
 * The standard first seven product columns (PR 10) — every product table leads
 * with the same seven, in the same order, with the same words, and reads the
 * same numbers (the product snapshot, GET /api/ops/products/snapshot):
 *
 *   Product · Stock (DOS) · Sales (30d) · Ad spend (30d) · ACoS · Net profit (30d) · Status
 *
 * WHY: the owner saw "how is this product doing" answered differently on every
 * screen — the hub, Listings, PPC Products and Restock each had their own
 * stock, sales and status columns, built from different sources and windows.
 * One definition here; one source behind it (stock and days of cover are the
 * Restock row's, money is the P&L's over the last 30 COMPLETE days, status is
 * the listing status with Amazon's own word winning on Amazon).
 *
 * Pure (no React) so the order and the wording are pinned by
 * productStandardColumns.test.ts. Each table engine has a thin adapter that
 * turns these specs into its own column type (ops/components/
 * standardProductCols.tsx for the ops EntityTable; the PPC Products page
 * builds its main-app columns from the same specs). Tables bake the snapshot
 * values onto their rows under `SNAPSHOT_ROW_FIELDS` so the columns sort,
 * filter and total like any other field.
 */
import type { ProductSnapshot } from '../ops/lib/api'
import { fmtInt } from './format'
import { metricLabel } from './metricGlossary'

/** The seven, in order. */
export const STANDARD_PRODUCT_COLUMN_IDS = [
  'product', 'stock_dos', 'sales_30d', 'ad_spend_30d', 'acos_30d', 'net_profit_30d', 'status',
] as const

export type StandardProductColumnId = (typeof STANDARD_PRODUCT_COLUMN_IDS)[number]

export interface StandardColumnSpec {
  id: StandardProductColumnId
  label: string
  /** Plain-English header tooltip. */
  tooltip: string
}

/** Where a table bakes each snapshot value on its rows (sort / filter / totals
 *  read a row field). `product` is the table's own name column. */
export const SNAPSHOT_ROW_FIELDS = {
  stock_dos: '__snap_stock_units',
  sales_30d: '__snap_sales_30d',
  ad_spend_30d: '__snap_ad_spend_30d',
  acos_30d: '__snap_acos_30d',
  net_profit_30d: '__snap_net_profit_30d',
  status: '__snap_status',
} as const

/** The baked row fields, one product's worth. */
export interface SnapshotRowFields {
  __snap: ProductSnapshot | null
  __snap_stock_units: number | null
  __snap_dos: number | null
  __snap_sales_30d: number | null
  __snap_ad_spend_30d: number | null
  __snap_ad_sales_30d: number | null
  __snap_acos_30d: number | null
  __snap_net_profit_30d: number | null
  __snap_status: string | null
}

/**
 * The seven column specs. `window`: '30d' (the default — the snapshot's last
 * 30 complete days, said in the label) or null when the table has its own
 * window control (the hub's tiles) and every column already covers that one
 * window — the suffix is dropped there, never mixed.
 */
export function standardProductColumns(opts: { window?: '30d' | null } = {}): StandardColumnSpec[] {
  const win = opts.window === undefined ? '30d' : opts.window
  // The labels come from the app-wide glossary (PR 9b): "Sales (30d)", not "Sales 30d".
  const over = win ? 'over the last 30 complete days' : 'in the selected window'
  return [
    {
      id: 'product', label: 'Product',
      tooltip: 'The product — its picture, name and ClickPay ID. Click the picture for a quick view of how it is doing.',
    },
    {
      id: 'stock_dos', label: metricLabel('stock_dos'),
      tooltip: 'Units we own (warehouse + other warehouses + Walmart WFS + FBA on hand + FBA inbound) and how many days they last at the current sales rate (days of supply). On an Amazon marketplace the days are that marketplace’s FBA stock ÷ its own sales. On-order stock is not included.',
    },
    {
      id: 'sales_30d', label: metricLabel('sales', win ?? undefined),
      tooltip: `Sales ${over}, from the P&L.`,
    },
    {
      id: 'ad_spend_30d', label: metricLabel('ad_spend', win ?? undefined),
      tooltip: `All advertising spend ${over}.`,
    },
    {
      id: 'acos_30d', label: metricLabel('acos'),
      tooltip: `Amazon ad spend ÷ Amazon ad-attributed sales (Sponsored Products, Brands, Display) ${over}.`,
    },
    {
      id: 'net_profit_30d', label: metricLabel('net_profit', win ?? undefined),
      tooltip: `What is left ${over} after cost of goods, marketplace fees, refunds and advertising.`,
    },
    {
      id: 'status', label: 'Status',
      tooltip: 'Listing status on this marketplace — Amazon’s own status wins over the GoFlow mirror. On All marketplaces: how many marketplaces it is active on.',
    },
  ]
}

/** One spec by id (the adapters look columns up this way). */
export function standardColumn(id: StandardProductColumnId, opts: { window?: '30d' | null } = {}): StandardColumnSpec {
  return standardProductColumns(opts).find((c) => c.id === id) as StandardColumnSpec
}

/** The values a table bakes onto a row for the standard columns. `status`
 *  is the marketplace's own status on a marketplace scope, the "Active on N
 *  of M" summary on the all-marketplace scope. */
export function snapshotRowFields(s: ProductSnapshot | null | undefined): SnapshotRowFields {
  return {
    __snap: s ?? null,
    __snap_stock_units: s?.stock_units ?? null,
    __snap_dos: s?.days_of_cover ?? null,
    __snap_sales_30d: s?.sales_30d ?? null,
    __snap_ad_spend_30d: s?.ad_spend_30d ?? null,
    __snap_ad_sales_30d: s?.ad_sales_30d ?? null,
    __snap_acos_30d: s?.acos_30d ?? null,
    __snap_net_profit_30d: s?.net_profit_30d ?? null,
    __snap_status: s ? (s.status ?? s.status_summary ?? null) : null,
  }
}

/** "2,100 · 210 d" — the stock the days count (pooled: everything we own
 *  plus what is on order, Restock's Total Stock; an Amazon tab: that
 *  marketplace's FBA stock), then days of supply ("∞" when nothing sells,
 *  "—" when unknown). */
export function stockDosText(units: number | null | undefined, days: number | null | undefined): string {
  if (units == null) return '—'
  const d = days == null
    ? (units > 0 ? '∞' : '—')
    : `${fmtInt(days)} d`
  return `${fmtInt(units)} · ${d}`
}

const POOL_LABEL: Record<string, string> = {
  warehouse: 'Warehouse',
  other_wh: 'Other warehouses',
  wfs: 'Walmart WFS',
  fba_on_hand: 'FBA on hand',
  fba_inbound: 'FBA inbound',
}

/** Hover text for a Stock (DOS) cell: the pools the stock figure is made
 *  of, then what the days mean — both on one basis. */
export function stockDosTitle(s: ProductSnapshot | null | undefined): string | undefined {
  if (!s) return undefined
  const fbaOnly = s.days_of_cover_basis === 'fba_this_marketplace'
  const lines: string[] = []
  for (const [k, label] of Object.entries(POOL_LABEL)) {
    const v = s.stock_breakdown?.[k]
    if (v) lines.push(`${label}: ${fmtInt(v)}`)
  }
  if (s.on_order) lines.push(fbaOnly ? `On order (not counted here): ${fmtInt(s.on_order)}` : `On order: ${fmtInt(s.on_order)}`)
  lines.push(fbaOnly
    ? 'Days: this marketplace’s FBA stock ÷ its own daily sales.'
    : 'Days: everything we own or have on order ÷ daily sales across every marketplace.')
  return lines.join('\n')
}

/** 'active' → 'Active', 'in_review' → 'In review', 'inactive' → 'Inactive';
 *  anything else (a summary like "Active on 3 of 4") passes through. */
export function statusText(status: string | null | undefined): string {
  if (!status) return '—'
  const k = status.toLowerCase()
  if (k === 'active') return 'Active'
  if (k === 'in_review') return 'In review'
  if (k === 'inactive') return 'Inactive'
  return status
}

/** Hover text for an all-marketplace status: one marketplace per line. */
export function statusesTitle(s: ProductSnapshot | null | undefined): string | undefined {
  if (!s || !s.statuses) return undefined
  const lines = Object.entries(s.statuses).map(([mp, st]) => `${mp}: ${statusText(st)}`)
  return lines.length ? lines.join('\n') : undefined
}
